import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";

const courseTags = [
  { label: "BLS", icon: "ri-heart-pulse-line", color: "from-purple-700 to-purple-900" },
  { label: "ACLS", icon: "ri-stethoscope-line", color: "from-purple-800 to-indigo-900" },
  { label: "PALS", icon: "ri-user-heart-line", color: "from-violet-700 to-purple-900" },
  { label: "CPR", icon: "ri-first-aid-kit-line", color: "from-brand-purple to-purple-800" },
  { label: "AED", icon: "ri-flashlight-line", color: "from-purple-600 to-violet-800" },
  { label: "First Aid", icon: "ri-shield-cross-line", color: "from-purple-700 to-purple-950" },
];

const courseDescriptions: Record<string, { title: string; desc: string; duration: string }> = {
  BLS: {
    title: "Basic Life Support",
    desc: "Master the skills healthcare professionals need — high-quality CPR for adults, children, and infants, plus AED operation in team-based scenarios.",
    duration: "~4 hours • Same-Day Cert",
  },
  ACLS: {
    title: "Advanced Cardiovascular Life Support",
    desc: "For healthcare providers managing cardiopulmonary arrest, strokes, and acute arrhythmias. Led by experienced RN instructors with real clinical insight.",
    duration: "~8 hours • Same-Day Cert",
  },
  PALS: {
    title: "Pediatric Advanced Life Support",
    desc: "Focused on assessing and managing critically ill infants and children. Hands-on scenarios with expert RN guidance throughout.",
    duration: "~8 hours • Same-Day Cert",
  },
  CPR: {
    title: "CPR for All Levels",
    desc: "Comprehensive CPR training for individuals, caregivers, and professionals. Learn proper technique, rescue breathing, and emergency response protocols.",
    duration: "~2 hours • Same-Day Cert",
  },
  AED: {
    title: "AED Training & Certification",
    desc: "Learn to confidently operate an Automated External Defibrillator in real emergencies. Paired with CPR for full cardiac response readiness.",
    duration: "~2 hours • Same-Day Cert",
  },
  "First Aid": {
    title: "First Aid Essentials",
    desc: "Bleeding control, fractures, burns, choking, allergic reactions — hands-on first aid training that prepares you for everyday emergencies.",
    duration: "~3 hours • Same-Day Cert",
  },
};

const stats = [
  { icon: "ri-medal-2-line", value: "500+", label: "Certified Students" },
  { icon: "ri-nurse-line", value: "RN Led", label: "Expert Instructors" },
  { icon: "ri-time-line", value: "Same Day", label: "Certification" },
  { icon: "ri-shield-check-line", value: "AHA", label: "Aligned Training" },
];

const galleryImages = [
  {
    src: "https://public.readdy.ai/ai/img_res/a23b80ceea0338dc84c520a80ddf5b7a.jpg",
    alt: "Hands-on CPR compression training",
    caption: "Chest Compression Technique",
  },
  {
    src: "https://readdy.ai/api/search-image?query=Black%20female%20RN%20instructor%20in%20black%20scrubs%20leaning%20over%20student%20demonstrating%20CPR%20hand%20placement%20correction%20on%20training%20mannequin%20small%20group%20adult%20students%20watching%20attentively%20diverse%20classroom%20bright%20professional%20medical%20training%20room%20candid%20realistic%20photography&width=600&height=400&seq=gallery-instructor-guidance-v2&orientation=landscape",
    alt: "RN instructor guiding CPR technique",
    caption: "RN Instructor Guidance",
  },
  {
    src: "https://readdy.ai/api/search-image?query=Hispanic%20man%20and%20Black%20woman%20working%20together%20practicing%20AED%20defibrillator%20pads%20placement%20on%20CPR%20training%20mannequin%20side%20by%20side%20realistic%20hands%20on%20learning%20community%20healthcare%20class%20modern%20bright%20training%20room%20candid%20photography&width=600&height=400&seq=gallery-aed-demo-v2&orientation=landscape",
    alt: "AED demonstration and training",
    caption: "AED Demonstration",
  },
  {
    src: "https://readdy.ai/api/search-image?query=diverse%20group%20Black%20Hispanic%20and%20mixed%20adult%20students%20seated%20in%20CPR%20certification%20class%20attentive%20engaged%20looking%20at%20instructor%20multiple%20mannequins%20on%20tables%20realistic%20community%20training%20room%20natural%20light%20professional%20healthcare%20education%20candid%20group%20photography&width=600&height=400&seq=gallery-classroom-v2&orientation=landscape",
    alt: "Active classroom participation",
    caption: "Active Classroom Learning",
  },
];

export default function TrainingInAction() {
  const [activeTag, setActiveTag] = useState("BLS");
  const [activeGallery, setActiveGallery] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [statsVisible, setStatsVisible] = useState(false);
  const [videoVisible, setVideoVisible] = useState(false);
  const [videoPlaying, setVideoPlaying] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);
  const statsRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
      { threshold: 0.15 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setStatsVisible(true); },
      { threshold: 0.3 }
    );
    if (statsRef.current) observer.observe(statsRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVideoVisible(true); },
      { threshold: 0.2 }
    );
    if (videoRef.current) observer.observe(videoRef.current);
    return () => observer.disconnect();
  }, []);

  // Auto-rotate gallery
  useEffect(() => {
    const timer = setInterval(() => {
      setActiveGallery((prev) => (prev + 1) % galleryImages.length);
    }, 4000);
    return () => clearInterval(timer);
  }, []);

  const activeCourse = courseDescriptions[activeTag];

  return (
    <section
      ref={sectionRef}
      className="relative overflow-hidden bg-brand-black py-24"
      style={{ background: "linear-gradient(135deg, #0d0d0d 0%, #1a0a2e 50%, #0d0d0d 100%)" }}
    >
      {/* Animated background particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(18)].map((_, i) => (
          <div
            key={i}
            className="absolute rounded-full opacity-10"
            style={{
              width: `${Math.random() * 6 + 2}px`,
              height: `${Math.random() * 6 + 2}px`,
              background: i % 2 === 0 ? "#D4AF37" : "#9F7AEA",
              left: `${(i * 5.5) % 100}%`,
              top: `${(i * 7.3) % 100}%`,
              animation: `floatParticle ${5 + (i % 5)}s ease-in-out infinite`,
              animationDelay: `${i * 0.4}s`,
            }}
          />
        ))}
      </div>

      {/* Gold line top */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-brand-gold to-transparent opacity-60" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">

        {/* Section Header */}
        <div
          className="text-center mb-16 transition-all duration-1000"
          style={{
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? "translateY(0)" : "translateY(40px)",
          }}
        >
          <div className="inline-flex items-center gap-2 bg-brand-gold/15 border border-brand-gold/30 text-brand-gold text-xs font-bold uppercase tracking-widest px-4 py-2 rounded-full mb-5">
            <i className="ri-play-circle-line" />
            See the Training in Action
          </div>
          <h2 className="font-serif text-4xl lg:text-5xl xl:text-6xl font-bold text-white leading-tight mb-5">
            Real Hands-On{" "}
            <span
              className="relative inline-block"
              style={{ background: "linear-gradient(90deg, #D4AF37, #f5d97a, #D4AF37)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}
            >
              CPR Training
            </span>
          </h2>
          <p className="text-white/70 text-lg max-w-2xl mx-auto leading-relaxed">
            Step inside a Cardiocraft CPR class. RN-led instruction, American Heart Association–aligned
            curriculum, and same-day certification — all in one premium training experience.
          </p>
        </div>

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-2 gap-12 items-start mb-16">

          {/* LEFT — Gallery & Video Player */}
          <div
            className="transition-all duration-1000 delay-200"
            style={{
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? "translateX(0)" : "translateX(-50px)",
            }}
          >
            {/* Main Gallery Image */}
            <div className="relative rounded-2xl overflow-hidden group" style={{ border: "1px solid rgba(212,175,55,0.3)" }}>
              {galleryImages.map((img, idx) => (
                <div
                  key={idx}
                  className="absolute inset-0 transition-opacity duration-700"
                  style={{ opacity: activeGallery === idx ? 1 : 0 }}
                >
                  <img
                    src={img.src}
                    alt={img.alt}
                    className="w-full object-cover object-top"
                    style={{ height: "400px" }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                </div>
              ))}

              {/* Active image (always visible) */}
              <div className="relative" style={{ height: "400px" }}>
                <img
                  src={galleryImages[activeGallery].src}
                  alt={galleryImages[activeGallery].alt}
                  className="w-full h-full object-cover object-top transition-all duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />

                {/* Caption overlay */}
                <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between">
                  <div className="flex items-center gap-2 bg-black/60 backdrop-blur-sm border border-white/10 px-3 py-2 rounded-lg">
                    <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                    <span className="text-white text-sm font-medium">{galleryImages[activeGallery].caption}</span>
                  </div>
                  {/* Gold play icon badge */}
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-brand-gold/20 border border-brand-gold/50 backdrop-blur-sm">
                    <i className="ri-live-line text-brand-gold text-sm" />
                  </div>
                </div>
              </div>

              {/* Hover shimmer */}
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
                style={{ background: "linear-gradient(135deg, transparent 40%, rgba(212,175,55,0.06) 100%)" }}
              />
            </div>

            {/* Gallery Thumbnails */}
            <div className="grid grid-cols-4 gap-2 mt-3">
              {galleryImages.map((img, idx) => (
                <button
                  key={idx}
                  onClick={() => setActiveGallery(idx)}
                  className="relative rounded-lg overflow-hidden cursor-pointer transition-all duration-300 focus:outline-none"
                  style={{
                    border: activeGallery === idx ? "2px solid #D4AF37" : "2px solid transparent",
                    opacity: activeGallery === idx ? 1 : 0.55,
                    transform: activeGallery === idx ? "scale(1.03)" : "scale(1)",
                    height: "70px",
                  }}
                >
                  <img
                    src={img.src}
                    alt={img.alt}
                    className="w-full h-full object-cover object-top"
                  />
                  {activeGallery === idx && (
                    <div className="absolute inset-0 bg-brand-gold/15" />
                  )}
                </button>
              ))}
            </div>

            {/* Dot indicators */}
            <div className="flex justify-center gap-2 mt-4">
              {galleryImages.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setActiveGallery(idx)}
                  className="rounded-full transition-all duration-300 cursor-pointer focus:outline-none"
                  style={{
                    width: activeGallery === idx ? "24px" : "8px",
                    height: "8px",
                    background: activeGallery === idx ? "#D4AF37" : "rgba(255,255,255,0.3)",
                  }}
                />
              ))}
            </div>
          </div>

          {/* RIGHT — Interactive Course Info */}
          <div
            className="transition-all duration-1000 delay-400"
            style={{
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? "translateX(0)" : "translateX(50px)",
            }}
          >
            {/* Subheading */}
            <p className="text-brand-gold text-sm font-bold uppercase tracking-widest mb-3">
              What We Teach
            </p>
            <h3 className="font-serif text-3xl font-bold text-white mb-4 leading-snug">
              Train With Confidence.<br />
              <span className="text-white/60">Certify the Same Day.</span>
            </h3>
            <p className="text-white/65 text-base leading-relaxed mb-8">
              Every Cardiocraft CPR course is led by a <strong className="text-white">Registered Nurse</strong> with real clinical experience.
              We go beyond the textbook — giving you the hands-on skills and confidence to respond
              when it matters most.
            </p>

            {/* Course Tag Selector */}
            <div className="mb-6">
              <p className="text-white/40 text-xs uppercase tracking-widest mb-3 font-semibold">Select a Course</p>
              <div className="flex flex-wrap gap-2">
                {courseTags.map((tag) => (
                  <button
                    key={tag.label}
                    onClick={() => setActiveTag(tag.label)}
                    className="relative flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold transition-all duration-300 cursor-pointer focus:outline-none whitespace-nowrap overflow-hidden"
                    style={{
                      background: activeTag === tag.label
                        ? "linear-gradient(135deg, #6B46C1, #4C1D95)"
                        : "rgba(255,255,255,0.06)",
                      border: activeTag === tag.label
                        ? "1px solid rgba(212,175,55,0.6)"
                        : "1px solid rgba(255,255,255,0.12)",
                      color: activeTag === tag.label ? "#fff" : "rgba(255,255,255,0.55)",
                      transform: activeTag === tag.label ? "scale(1.05)" : "scale(1)",
                    }}
                  >
                    <i className={`${tag.icon} text-xs ${activeTag === tag.label ? "text-brand-gold" : ""}`} />
                    {tag.label}
                    {activeTag === tag.label && (
                      <span className="absolute inset-0 opacity-20 rounded-full"
                        style={{ background: "linear-gradient(135deg, #D4AF37, transparent)" }}
                      />
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Active Course Details Card */}
            <div
              key={activeTag}
              className="rounded-xl p-6 mb-8 transition-all duration-500"
              style={{
                background: "rgba(107,70,193,0.12)",
                border: "1px solid rgba(212,175,55,0.25)",
                animation: "fadeSlideIn 0.4s ease",
              }}
            >
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 flex items-center justify-center rounded-lg shrink-0"
                  style={{ background: "linear-gradient(135deg, #6B46C1, #4C1D95)" }}>
                  <i className={`${courseTags.find((t) => t.label === activeTag)?.icon ?? "ri-heart-pulse-line"} text-brand-gold text-lg`} />
                </div>
                <div className="flex-1">
                  <h4 className="text-white font-bold text-lg mb-1 font-serif">{activeCourse.title}</h4>
                  <p className="text-white/65 text-sm leading-relaxed mb-3">{activeCourse.desc}</p>
                  <div className="inline-flex items-center gap-1.5 bg-brand-gold/10 border border-brand-gold/30 text-brand-gold text-xs font-semibold px-3 py-1.5 rounded-full">
                    <i className="ri-time-line" />
                    {activeCourse.duration}
                  </div>
                </div>
              </div>
            </div>

            {/* Trust Bullet Points */}
            <div className="space-y-3 mb-8">
              {[
                { icon: "ri-nurse-line", text: "Registered Nurse instructors with real clinical experience" },
                { icon: "ri-shield-check-line", text: "American Heart Association–aligned curriculum" },
                { icon: "ri-medal-line", text: "Same-day certification upon successful completion" },
                { icon: "ri-group-line", text: "Individual, group, and corporate training available" },
              ].map((item) => (
                <div key={item.text} className="flex items-center gap-3">
                  <div className="w-8 h-8 flex items-center justify-center rounded-full shrink-0"
                    style={{ background: "rgba(212,175,55,0.15)", border: "1px solid rgba(212,175,55,0.3)" }}>
                    <i className={`${item.icon} text-brand-gold text-sm`} />
                  </div>
                  <span className="text-white/75 text-sm leading-snug">{item.text}</span>
                </div>
              ))}
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-wrap gap-3">
              <Link
                to="/booking#reserve-spot"
                className="flex items-center gap-2 font-bold text-sm px-6 py-3.5 rounded-lg cursor-pointer whitespace-nowrap transition-all duration-300 hover:scale-105 hover:shadow-lg"
                style={{
                  background: "linear-gradient(135deg, #D4AF37, #b8952e)",
                  color: "#1A1A1A",
                  boxShadow: "0 4px 24px rgba(212,175,55,0.3)",
                }}
              >
                <i className="ri-calendar-check-line" />
                Book Your Class Today
              </Link>
              <Link
                to="/courses"
                className="flex items-center gap-2 font-semibold text-sm px-6 py-3.5 rounded-lg cursor-pointer whitespace-nowrap transition-all duration-300 hover:bg-white/10"
                style={{
                  border: "1px solid rgba(255,255,255,0.2)",
                  color: "rgba(255,255,255,0.85)",
                }}
              >
                Explore Courses
                <i className="ri-arrow-right-line" />
              </Link>
            </div>
          </div>
        </div>

        {/* ── VIDEO EMBED PANEL ── */}
        <div
          ref={videoRef}
          className="mb-16 transition-all duration-1000 delay-300"
          style={{
            opacity: videoVisible ? 1 : 0,
            transform: videoVisible ? "translateY(0)" : "translateY(40px)",
          }}
        >
          {/* Label */}
          <div className="flex items-center justify-center gap-3 mb-8">
            <div className="h-px flex-1 bg-gradient-to-r from-transparent to-brand-gold/40" />
            <div className="inline-flex items-center gap-2 bg-brand-gold/10 border border-brand-gold/30 text-brand-gold text-xs font-bold uppercase tracking-widest px-4 py-2 rounded-full whitespace-nowrap">
              <i className="ri-live-line" />
              Live Training Footage
            </div>
            <div className="h-px flex-1 bg-gradient-to-l from-transparent to-brand-gold/40" />
          </div>

          {/* Video Container */}
          <div
            className="relative rounded-2xl overflow-hidden mx-auto max-w-4xl group"
            style={{ border: "1px solid rgba(212,175,55,0.35)" }}
          >
            {/* Glow ring */}
            <div
              className="absolute -inset-px rounded-2xl pointer-events-none"
              style={{ boxShadow: "0 0 40px rgba(107,70,193,0.25), 0 0 80px rgba(212,175,55,0.08)" }}
            />

            {!videoPlaying ? (
              /* Thumbnail / Cover State */
              <div className="relative cursor-pointer" style={{ paddingBottom: "56.25%" }} onClick={() => setVideoPlaying(true)}>
                <img
                  src="https://readdy.ai/api/search-image?query=diverse%20Black%20and%20Hispanic%20adult%20students%20actively%20performing%20CPR%20chest%20compressions%20on%20training%20mannequins%20in%20a%20professional%20certification%20classroom%20Black%20female%20RN%20nurse%20instructor%20in%20black%20scrubs%20supervising%20in%20background%20natural%20overhead%20lighting%20realistic%20candid%20training%20moment%20AHA%20aligned%20CPR%20class%20Kissimmee%20Florida&width=896&height=504&seq=vid-thumb-v2&orientation=landscape"
                  alt="CPR training video thumbnail"
                  className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                />
                {/* Dark overlay */}
                <div className="absolute inset-0 bg-black/50 group-hover:bg-black/40 transition-colors duration-300" />

                {/* Center play button */}
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-4">
                  <button
                    className="relative flex items-center justify-center w-20 h-20 rounded-full cursor-pointer transition-all duration-300 hover:scale-110 focus:outline-none"
                    style={{
                      background: "linear-gradient(135deg, #D4AF37, #b8952e)",
                      boxShadow: "0 0 0 12px rgba(212,175,55,0.15), 0 8px 32px rgba(212,175,55,0.4)",
                    }}
                    onClick={() => setVideoPlaying(true)}
                  >
                    <i className="ri-play-fill text-black text-3xl ml-1" />
                    {/* Pulse ring */}
                    <span
                      className="absolute inset-0 rounded-full animate-ping"
                      style={{ background: "rgba(212,175,55,0.25)" }}
                    />
                  </button>
                  <div className="text-center">
                    <p className="text-white font-bold text-xl font-serif drop-shadow-lg">Hands-Only CPR with an AED</p>
                    <p className="text-white/70 text-sm mt-1">American Heart Association–Aligned Instruction</p>
                  </div>
                </div>

                {/* Badge bottom-left */}
                <div className="absolute bottom-4 left-4 flex items-center gap-2 bg-black/70 backdrop-blur-sm border border-white/10 px-3 py-2 rounded-lg">
                  <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                  <span className="text-white text-xs font-semibold">Hands-Only CPR with an AED</span>
                </div>

                {/* Badge bottom-right */}
                <div className="absolute bottom-4 right-4 flex items-center gap-2 bg-brand-gold/20 backdrop-blur-sm border border-brand-gold/40 px-3 py-2 rounded-lg">
                  <i className="ri-nurse-line text-brand-gold text-xs" />
                  <span className="text-brand-gold text-xs font-semibold">RN-Led Instruction</span>
                </div>
              </div>
            ) : (
              /* Active Video Embed */
              <div className="relative" style={{ paddingBottom: "56.25%" }}>
                <iframe
                  className="absolute inset-0 w-full h-full"
                  src="https://www.youtube.com/embed/M7I3mJTy4RI?autoplay=1&rel=0&modestbranding=1&color=white"
                  title="Hands-Only CPR with an AED — Cardiocraft CPR"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            )}
          </div>

          {/* Below-video info strip */}
          <div className="flex flex-wrap items-center justify-center gap-6 mt-6">
            {[
              { icon: "ri-heart-pulse-line", text: "Hands-Only CPR Technique" },
              { icon: "ri-nurse-line", text: "RN Instructor Guidance" },
              { icon: "ri-shield-check-line", text: "AHA-Aligned Curriculum" },
              { icon: "ri-medal-2-line", text: "Same-Day Certification" },
            ].map((item) => (
              <div key={item.text} className="flex items-center gap-2 text-white/60 text-sm">
                <i className={`${item.icon} text-brand-gold`} />
                <span>{item.text}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom Stats Bar */}
        <div
          ref={statsRef}
          className="grid grid-cols-2 lg:grid-cols-4 gap-4 transition-all duration-1000 delay-600"
          style={{
            opacity: statsVisible ? 1 : 0,
            transform: statsVisible ? "translateY(0)" : "translateY(30px)",
          }}
        >
          {stats.map((stat, idx) => (
            <div
              key={stat.label}
              className="group relative text-center py-7 px-4 rounded-xl cursor-default overflow-hidden transition-all duration-300 hover:-translate-y-1"
              style={{
                background: "rgba(255,255,255,0.04)",
                border: "1px solid rgba(255,255,255,0.08)",
                animationDelay: `${idx * 0.15}s`,
              }}
            >
              {/* Hover glow */}
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none rounded-xl"
                style={{ background: "linear-gradient(135deg, rgba(107,70,193,0.15), rgba(212,175,55,0.08))" }} />
              <div
                className="w-12 h-12 flex items-center justify-center rounded-full mx-auto mb-3 transition-transform duration-300 group-hover:scale-110"
                style={{ background: "linear-gradient(135deg, rgba(107,70,193,0.3), rgba(107,70,193,0.15))", border: "1px solid rgba(212,175,55,0.3)" }}
              >
                <i className={`${stat.icon} text-brand-gold text-xl`} />
              </div>
              <div className="text-2xl font-bold text-white font-serif mb-1">{stat.value}</div>
              <div className="text-white/50 text-xs uppercase tracking-widest font-medium">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Gold line bottom */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-brand-gold to-transparent opacity-60" />

      <style>{`
        @keyframes floatParticle {
          0%, 100% { transform: translateY(0px) translateX(0px); }
          33% { transform: translateY(-20px) translateX(8px); }
          66% { transform: translateY(-10px) translateX(-8px); }
        }
        @keyframes fadeSlideIn {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </section>
  );
}
